import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Timer as TimerIcon, Play, Pause, RotateCcw, Coffee, Brain } from "lucide-react";
import { EMBED } from "@/constants/testIds";

/**
 * Jixxeo Timer — a neon Pomodoro embedded inside Jixxeo. 25-min focus /
 * 5-min break with a countdown ring, tick option, and a coin celebration
 * (badge only — coin awarding runs through the play-time-rewards loop).
 */

const FOCUS_S = 25 * 60;
const BREAK_S = 5 * 60;

export default function JixxeoTimer() {
  const [phase, setPhase] = useState("focus"); // focus | break
  const [remaining, setRemaining] = useState(FOCUS_S);
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [tick, setTick] = useState(false);
  const audioCtxRef = useRef(null);
  const raf = useRef(null);
  const lastTs = useRef(0);
  const tickTs = useRef(0);

  const total = phase === "focus" ? FOCUS_S : BREAK_S;
  const pct = 1 - remaining / total;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(Math.floor(remaining % 60)).padStart(2, "0");
  const accent = phase === "focus" ? "#00F0FF" : "#39FF14";

  const beep = (freq = 880, dur = 0.18) => {
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.type = "sine";
      g.gain.setValueAtTime(0, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + dur + 0.02);
    } catch {}
  };

  const chime = () => {
    beep(880, 0.15);
    setTimeout(() => beep(1320, 0.2), 160);
    setTimeout(() => beep(1760, 0.35), 340);
  };

  useEffect(() => {
    if (!running) return;
    const step = (ts) => {
      if (!lastTs.current) lastTs.current = ts;
      const dt = (ts - lastTs.current) / 1000;
      lastTs.current = ts;
      setRemaining((r) => {
        const n = Math.max(0, r - dt);
        if (tick && ts - tickTs.current > 1000) {
          tickTs.current = ts;
          beep(500, 0.03);
        }
        if (n <= 0) {
          chime();
          if (phase === "focus") {
            setCompleted((c) => c + 1);
            setPhase("break");
            return BREAK_S;
          }
          setPhase("focus");
          return FOCUS_S;
        }
        return n;
      });
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf.current);
      lastTs.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phase, tick]);

  const toggle = () => setRunning((r) => !r);
  const reset = () => { setRunning(false); setRemaining(phase === "focus" ? FOCUS_S : BREAK_S); };
  const switchPhase = (p) => { setRunning(false); setPhase(p); setRemaining(p === "focus" ? FOCUS_S : BREAK_S); };

  const R = 120;
  const C = 2 * Math.PI * R;

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10 relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        background: `radial-gradient(ellipse at 50% 20%, ${accent}22, transparent 60%)`,
      }} />
      <div className="flex items-center gap-3 mb-6 relative">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_0_24px_rgba(0,240,255,0.4)]"
          style={{ background: accent }}>
          <TimerIcon className="w-5 h-5 text-black" strokeWidth={3} />
        </span>
        <div>
          <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
          <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter leading-none">Jixxeo Timer</h1>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => switchPhase("focus")} className={`flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${phase === "focus" ? "border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]" : "border-white/15 text-white/60 hover:text-white"}`}>
            <Brain className="w-3 h-3" /> Focus
          </button>
          <button onClick={() => switchPhase("break")} className={`flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${phase === "break" ? "border-[#39FF14] bg-[#39FF14]/15 text-[#39FF14]" : "border-white/15 text-white/60 hover:text-white"}`}>
            <Coffee className="w-3 h-3" /> Break
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative">
        <motion.svg width="300" height="300" viewBox="0 0 300 300"
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <defs>
            <filter id="glow"><feGaussianBlur stdDeviation="4" /></filter>
          </defs>
          <circle cx="150" cy="150" r={R} stroke="rgba(255,255,255,0.06)" strokeWidth="14" fill="none" />
          <circle cx="150" cy="150" r={R} stroke={accent} strokeWidth="14" fill="none"
            strokeLinecap="round" strokeDasharray={C}
            strokeDashoffset={C * (1 - pct)}
            transform="rotate(-90 150 150)"
            filter="url(#glow)"
            style={{ transition: "stroke-dashoffset 0.3s linear" }} />
        </motion.svg>
        <div className="absolute text-center">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-white/50">{phase === "focus" ? "// deep work" : "// recharge"}</div>
          <div className="font-heading font-black text-6xl md:text-7xl uppercase tracking-tighter tabular-nums mt-1" style={{ color: accent }}>
            {mm}:{ss}
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/40 mt-1">
            {completed} pomodoros · today
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 mt-8">
        <button onClick={reset}
          className="w-12 h-12 rounded-full border border-white/15 hover:border-white/40 flex items-center justify-center text-white/70">
          <RotateCcw className="w-5 h-5" />
        </button>
        <button data-testid={EMBED.timerStart} onClick={toggle}
          className="w-16 h-16 rounded-full text-black font-black flex items-center justify-center pulse-neon"
          style={{ background: accent, boxShadow: `0 0 40px ${accent}66` }}>
          {running ? <Pause className="w-7 h-7" fill="#000" /> : <Play className="w-7 h-7" fill="#000" />}
        </button>
        <button onClick={() => setTick((t) => !t)}
          className={`w-12 h-12 rounded-full border flex items-center justify-center mono text-[9px] uppercase tracking-widest font-bold transition-colors ${tick ? "border-[#FFE300] text-[#FFE300] bg-[#FFE300]/10" : "border-white/15 text-white/60"}`}
          title="Tick sound">
          tik
        </button>
      </div>

      <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-6 text-center">
        neon pomodoro · earn play-time coins as you focus
      </div>
    </div>
  );
}
